import { useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
  Path,
  useFormContext,
} from 'react-hook-form';
import { authProvider, stateLicenseProvider } from '@/providers';
import * as yup from 'yup';
import { MyDialog, MyButton, LabelComponent, FormSelect, Loader } from '@/components/uiComponents';
import {
  HeaderDialogsEnum,
  UserKindEnum,
  LicensesGetAgenciesResponseType,
  UserAgencyStateLicense,
  NotificationTypeEnum,
} from '@/types';
import { DialogsType } from '@/components/uiComponents/types';
import { Autocomplete, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import { StyledIconButton, StyledPhoneInput, StyleLabel } from '@/components/uiComponents/styled';
import { schemaCreatePassword } from '../NewSignUp/Utils/schemas';
import { useDispatch } from 'react-redux';
import { appSetNotification } from '@/store';

const userTypeValue = [
  {
    label: 'Licensed Agent',
    value: UserKindEnum.Broker,
  },
  {
    label: 'Agency Manager',
    value: UserKindEnum.Manager,
  },
  {
    label: 'Real estate agency',
    value: UserKindEnum.Agency,
  },
  {
    label: 'Developer',
    value: UserKindEnum.Developer,
  },
];

interface SignUpFormFormModel {
  userType: string;
  brokerVerification?: string;
  agencyVerification?: string;
  developerVerification?: string;
  userName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
  registryId?: string;
}

const baseStyle = { fontSize: 18, fontWeight: 600, textAlign: 'center' };

const SignUp = ({
  openDialog,
  handleOpenDialog,
}: {
  openDialog: DialogsType;
  handleOpenDialog: (value: HeaderDialogsEnum) => void;
}) => {
  const dispatch = useDispatch();
  const [autocompleteValue, setAutocompleteValue] = useState<string>('');
  const [brokers, setBrokers] = useState<any[]>([]);
  const [managerAgencies, setManagerAgencies] = useState<any[]>([]);
  const [agencies, setAgencies] = useState<any[]>([]);
  const [developers, setDevelopers] = useState<any[]>([]);
  const [dldUser, setDldUser] = useState<UserAgencyStateLicense | null>();
  const [managerName, setManagerName] = useState<string>('');
  const [managerEmail, setManagerEmail] = useState<string>('');
  const [managerPhone, setManagerPhone] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [helperText, setHelperText] = useState<string>('');
  const [openSuccess, setOpenSuccess] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>('');
  const [openError, setOpenError] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formMethods = useForm<SignUpFormFormModel>({
    defaultValues: {
      userType: '',
      brokerVerification: '',
      agencyVerification: '',
      developerVerification: '',
      userName: '',
      companyName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      registryId: '',
    },
  });
  const {
    handleSubmit,
    setValue,
    reset,
    watch,
  } = formMethods;
  const userType = watch('userType');
  const phone = watch('phone');
  const isManager = userType === UserKindEnum.Manager;
  const isAgency = userType === UserKindEnum.Agency;
  const isBroker = userType === UserKindEnum.Broker;
  const isDeveloper = userType === UserKindEnum.Developer;
  const isPasswordVisible =
    (isManager &&
      managerName?.length > 0 &&
      managerEmail?.length > 0 &&
      managerPhone?.length > 0) ||
    (!isManager && dldUser && (dldUser?.phone?.length > 0 || (phone && phone?.length > 0)));

  const switchUserType = (param: string) => {
    let result;
    switch (userType) {
      case UserKindEnum.Broker:
        result = stateLicenseProvider.searchBroker({ query: param });
        break;
      case UserKindEnum.Agency:
        result = stateLicenseProvider.searchAgency({ query: param });
        break;
      case UserKindEnum.Developer:
        result = stateLicenseProvider.searchDeveloper({ query: param });
        break;
      case UserKindEnum.Manager:
        result = stateLicenseProvider.searchAgency({ query: param });
        break;
      case UserKindEnum.Foreign:
        break;
      default:
        console.log(`Unknown action: ${userType}`);
    }
    return result;
  };

  const handleOnChangeAutocomplete = async (param: string) => {
    if (param.length < 2) return;
    const result = await switchUserType(param);
    if (result?.ok && result?.data) {
      const { items } = result.data as any;
      const optionsBrokerAndAgency = Array.from(
        new Set(
          items
            ?.flatMap((el: any) => [
              el.stateId?.includes(param) ? el.stateId : null,
              el.nameEn?.toLowerCase().includes(param.toLowerCase()) ? el.nameEn : null,
              el.phone?.includes(param) ? el.phone : null,
              el.email?.toLowerCase().includes(param.toLowerCase()) ? el.email : null,
            ])
            .filter(Boolean)
        )
      ) as string[];
      if (isBroker) {
        setBrokers(optionsBrokerAndAgency);
      } else if (isManager) {
        setManagerAgencies(optionsBrokerAndAgency);
      } else if (isAgency) {
        setAgencies(optionsBrokerAndAgency);
      } else if (isDeveloper) {
        setDevelopers(optionsBrokerAndAgency);
      }
    }
  };

  useEffect(() => {
    const fetchRes = async () => {
      if (!autocompleteValue) return;
      try {
        const result = await switchUserType(autocompleteValue);
        if (result?.ok && result?.data) {
          const { data, status, message } = result;
          const { items } = data as LicensesGetAgenciesResponseType;
          const user = items[0];
          setDldUser(user);
          if (!isManager) {
            setValue('email', user.email);
            setValue('phone', user.phone);
            setValue('userName', user.nameEn);
            setValue('companyName', user?.agency?.nameEn);
            setValue('registryId', user?.id);
            setUserName(user.nameEn);
          } else {
            setValue('companyName', user.nameEn);
            setValue('registryId', user?.id);
          }
        }
      } catch (error) {
        console.error('Error request:', error);
      }
    };
    fetchRes();
  }, [autocompleteValue]);

  const clearState = () => {
    setBrokers([]);
    setManagerAgencies([]);
    setAgencies([]);
    setDevelopers([]);
    setDldUser(null);
    setManagerName('');
    setManagerEmail('');
    setManagerPhone('');
    setPassword('');
    setConfirmPassword('');
  };

  useEffect(() => {
    setValue('agencyVerification', undefined);
    setValue('brokerVerification', undefined);
    setValue('developerVerification', undefined);
    clearState();
  }, [userType]);

  const submitHandler: SubmitHandler<SignUpFormFormModel> = async (values) => {
    try {
      await schemaCreatePassword.validate({
        password,
        confirmPassword,
      });
      if (password && values) {
        const dataSubmit = {
          kind: values.userType as UserKindEnum,
          phone: values.phone || '',
          email: values.email || '',
          password: values.password || '',
          name: values.userName || '',
          registryId: values.registryId || null,
        };
        setIsLoading(true);
        const response = await authProvider.signup(dataSubmit);
        const { ok, message, status } = response;
        if (ok) {
          setOpenSuccess(true);
          clearState();
          reset();
          handleOpenDialog(HeaderDialogsEnum.SignUp);
        } else {
          dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
          setOpenError(true);
          setErrorText(message || 'Server error');
        }
        setIsLoading(false);
      }
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setHelperText(err.errors[0]);
        setError(true);
      }
    }
  };

  const onErrorClose = () => {
    setOpenError(false);
    setErrorText('');
    clearState();
    reset();
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <MyDialog
        open={openDialog.signUp}
        onClose={() => {
          reset();
          clearState();
          handleOpenDialog(HeaderDialogsEnum.SignUp);
        }}
        dialogTitle='Registration'
        width='500'
      >
        <Stack>
          <FormProvider {...formMethods}>
            <Stack spacing={2}>
              <Stack spacing={1}>
                <MyLabel label='Select user type' />
                <FormSelect uiVariant='blank' name='userType' data={userTypeValue} />
              </Stack>
              {isBroker && (
                <DldSearchAutocomplete
                  options={brokers}
                  onInputChange={handleOnChangeAutocomplete}
                  label='Your full name'
                  name='brokerVerification'
                  placeholder='Enter your full name or license data'
                  setAutocompleteValue={setAutocompleteValue}
                />
              )}
              {isManager && (
                <DldSearchAutocomplete
                  options={managerAgencies}
                  onInputChange={handleOnChangeAutocomplete}
                  label='Your company full name'
                  name='agencyVerification'
                  placeholder='Enter your company full name or license data'
                  setAutocompleteValue={setAutocompleteValue}
                />
              )}
              {isAgency && (
                <DldSearchAutocomplete
                  options={agencies}
                  onInputChange={handleOnChangeAutocomplete}
                  label='Your full name'
                  name='agencyVerification'
                  placeholder='Enter your full name or license data'
                  setAutocompleteValue={setAutocompleteValue}
                />
              )}
              {isDeveloper && (
                <DldSearchAutocomplete
                  options={developers}
                  onInputChange={handleOnChangeAutocomplete}
                  label='Your full name'
                  name='developerVerification'
                  placeholder='Enter your full name or license data'
                  setAutocompleteValue={setAutocompleteValue}
                />
              )}
              {dldUser && !isManager && (
                <Stack>
                  <MyLabel label='Check details' />
                  <Stack spacing={1}>
                    <Stack spacing={1}>
                      <LabelComponent label={isBroker ? 'Your full name' : 'Company name'} />
                      <TextField value={dldUser?.nameEn} disabled fullWidth />
                    </Stack>
                    <Stack spacing={1}>
                      <LabelComponent label={isBroker ? 'Your agency' : 'Office number'} />
                      <TextField
                        value={isBroker ? dldUser?.agency?.nameEn : dldUser.stateId}
                        disabled
                        fullWidth
                      />
                    </Stack>
                    <Stack spacing={1}>
                      <LabelComponent label='Your email' />
                      <TextField value={dldUser?.email} disabled fullWidth />
                    </Stack>
                    <Stack spacing={1}>
                      <LabelComponent label='Your phone number' />
                      <StyledPhoneInput
                        name='phone'
                        defaultCountry='AE'
                        value={dldUser?.phone}
                        disabled={dldUser?.phone ? true : false}
                        onChange={(e: string) => setValue('phone', e)}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              )}
              {isManager && managerAgencies.length > 0 && (
                <Stack>
                  <MyLabel label='Contact data' />
                  <Stack spacing={1}>
                    <Stack spacing={1}>
                      <LabelComponent label={'Your full name'} />
                      <TextField
                        name='userName'
                        value={managerName}
                        fullWidth
                        onChange={(e) => {
                          const val = e.target.value;
                          setValue('userName', val);
                          setManagerName(val);
                          setUserName(val);
                        }}
                      />
                    </Stack>
                    <Stack spacing={1}>
                      <LabelComponent label='Your company name' />
                      <TextField value={dldUser?.nameEn || ''} disabled fullWidth />
                    </Stack>
                    <Stack spacing={1}>
                      <LabelComponent label='Your email' />
                      <TextField
                        name='email'
                        value={managerEmail}
                        fullWidth
                        onChange={(e) => {
                          const val = e.target.value;
                          setValue('email', val);
                          setManagerEmail(val);
                        }}
                      />
                    </Stack>
                    <Stack spacing={1}>
                      <LabelComponent label='Your phone number' />
                      <StyledPhoneInput
                        name='phone'
                        defaultCountry='AE'
                        value={managerPhone}
                        onChange={(e: string) => {
                          setValue('phone', e);
                          setManagerPhone(e);
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              )}
              {isPasswordVisible && (
                <Stack spacing={1}>
                  <MyLabel label='Create password' />
                  <PasswordComponent
                    name='password'
                    label='Create password'
                    placeholder='Create your password'
                    onChange={setPassword}
                    error={error}
                    helperText={helperText}
                  />
                  <PasswordComponent
                    name='confirmPassword'
                    label='Confirm password'
                    placeholder='Confirm your password'
                    onChange={setConfirmPassword}
                    error={error}
                    helperText={helperText}
                  />
                </Stack>
              )}
              <MyButton
                data={{
                  buttonName: 'Get started!',
                  variant: 'contained',
                  buttonType: 'submit',
                }}
                disabled={!(password.length > 0 && confirmPassword.length > 0)}
                onClick={handleSubmit(submitHandler)}
              />
            </Stack>
          </FormProvider>
        </Stack>
      </MyDialog>
      <MyDialog
        open={openSuccess}
        onClose={() => setOpenSuccess(false)}
        dialogTitle='Success'
        width='400'
      >
        <Stack spacing={4} sx={{ alignItems: 'center' }}>
          <Stack spacing={1}>
            <Typography sx={baseStyle}>User</Typography>
            <Typography sx={{ ...baseStyle, color: '#015cd5' }}>{userName}</Typography>
            <Typography sx={baseStyle}>was successfully registered</Typography>
          </Stack>
          <MyButton
            data={{
              buttonName: 'Ok',
              variant: 'contained',
              buttonType: 'submit',
              customWidth: '180px',
            }}
            onClick={() => setOpenSuccess(false)}
          />
        </Stack>
      </MyDialog>
      <MyDialog open={openError} onClose={onErrorClose} dialogTitle='Error' width='400'>
        <StyleLabel style={{ textAlign: 'center', color: 'red' }}>
          {errorText}
        </StyleLabel>
      </MyDialog>
    </>
  );
};

export default SignUp;

const MyLabel = ({ label }: { label: string }) => {
  return (
    <Typography sx={{ fontSize: '18px', fontWeight: 600, textAlign: 'center' }}>{label}</Typography>
  );
};

const DldSearchAutocomplete = ({
  options,
  onInputChange,
  label,
  name,
  placeholder,
  setAutocompleteValue,
}: {
  options: string[];
  onInputChange: (value: string) => void;
  label: string;
  name: Path<SignUpFormFormModel>;
  placeholder: string;
  setAutocompleteValue: (value: string) => void;
}) => {
  const { setValue, control } = useFormContext<SignUpFormFormModel>();

  return (
    <Stack spacing={1}>
      <MyLabel label='Find Yourself in the DLD License List' />
      <Stack>
        <LabelComponent label={label} />
        <Controller
          name={name}
          control={control}
          render={() => (
            <Autocomplete
              id='combo-box-demo'
              options={options}
              onInputChange={(event, newValue) => onInputChange(newValue)}
              onChange={(_, value) => {
                if (value) {
                  setValue(name, value);
                  setAutocompleteValue(value);
                } else {
                  setValue(name, '');
                  setAutocompleteValue('');
                }
              }}
              getOptionLabel={(option) => option}
              style={{
                width: '100%',
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label=''
                  placeholder={placeholder}
                  sx={{
                    marginTop: '2px',
                    height: 48,
                    '.MuiInputBase-input': {
                      height: 'calc(100% - 20px)',
                      boxSizing: 'border-box',
                    },
                    '.MuiInputBase-root': {
                      height: '100%',
                    },
                  }}
                />
              )}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};

const PasswordComponent = ({
  label,
  name,
  placeholder,
  onChange,
  error,
  helperText,
}: {
  label: string;
  name: Path<SignUpFormFormModel>;
  placeholder: string;
  onChange: (value: string) => void;
  error: boolean;
  helperText: string;
}) => {
  const { setValue, control } = useFormContext<SignUpFormFormModel>();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const IconComponent = showPassword ? VisibilityOffOutlined : VisibilityOutlined;

  return (
    <Stack spacing={1}>
      <LabelComponent label={label} />
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          return (
            <TextField
              {...field}
              variant='outlined'
              fullWidth
              placeholder={placeholder}
              inputProps={{ maxLength: 20 }}
              type={showPassword ? 'text' : 'password'}
              error={error}
              helperText={error ? helperText : null}
              // onClick={() => setError(false)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e?.target;
                onChange(value);
                setValue(name, value);
                field.onChange(e);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <StyledIconButton
                      data-qa='open_pass_btn'
                      aria-label='toggle password visibility'
                      onClick={() => setShowPassword && setShowPassword((prev) => !prev)}
                      edge='end'
                    >
                      <IconComponent />
                    </StyledIconButton>
                  </InputAdornment>
                ),
              }}
            />
          );
        }}
      />
    </Stack>
  );
};
